/*
  Theme: the MOBILE product gallery.

  ⚠️ MOBILE ONLY, AND INERT ABOVE 750px. The desktop gallery is finished and
  verified: a two-column grid of tiles inside a sticky column, with tap to zoom.
  Nothing in this file may touch it. Every behaviour below is gated on the same
  750px media query the stylesheet uses, and `teardown()` puts back anything
  this file changed the moment the viewport crosses back above it.

  WHAT IT DOES NOT DO: swiping. The track is a native scroll-snap container,
  exactly like .hp-rail on the home page. The browser's own touch scrolling
  gives momentum, rubber-banding and per-platform feel, and reimplementing that
  with pointer events is how carousels end up feeling wrong. This file only
  reacts to scrolling that has already happened.

  FOUR JOBS:
    1. Keep the thumbnail row in step with the slide in view.
    2. Keep the ACTIVE THUMBNAIL CENTRED in the rail as the slide changes.
    3. Move the track when a thumbnail is chosen.
    4. Move the track when a colour swatch is chosen, via a custom event.

  ⚠️ ACTIVE DETECTION IS AN IntersectionObserver, NOT `scrollend`, AND NOT THE
  SCROLL LISTENER ALONE. Added 12 August 2026 with job 2, because job 2 makes
  the timing matter in a way job 1 never did: painting a highlight a beat late
  is invisible, scrolling the rail a beat late is not.

    - `scrollend` is deliberately unused. It is the obvious tool and its
      support on iOS Safari is too recent to depend on, and the failure mode of
      depending on it is the rail simply never moving, silently, on the exact
      platform this feature is for.
    - An IntersectionObserver rooted on the track fires reliably during iOS
      momentum scrolling, which is when the rail needs to move.
    - The original throttled scroll listener is KEPT as a fallback, for engines
      with no IntersectionObserver and as a settle backstop. Both routes funnel
      through setActive(), which is idempotent on the index, so having two
      sources cannot double-scroll the rail.

  ⚠️ THE SEAM WITH assets/core-product.js IS AN EVENT, NOT A FUNCTION HANDLE.
  That file owns the variant logic and used to scroll the gallery itself, on the
  vertical axis. It now dispatches `ats:gallery:goto` with a mediaId and stops
  caring which axis the gallery moves on. An event rather than a shared object
  means neither file has to load first, and on desktop, where this script binds
  nothing, the event simply goes unheard while core-product.js does its own
  vertical scroll as before.

  ⚠️ TAB STOPS. On mobile the main tiles are given tabindex="-1", so the
  thumbnails are the single keyboard route to the images. Without that, every
  photograph would be two tab stops with one destination, which is the fault the
  product card's stretched link was built to avoid, multiplied by the image
  count. The attribute is removed again on desktop, where the tiles are the
  lightbox triggers and must stay reachable.
*/
(function () {
  'use strict';

  var MOBILE = '(max-width: 750px)';
  var SCROLL_THROTTLE_MS = 80;
  /* How long after the shopper stops touching the rail before the rail is
     allowed to move itself again. Long enough that a flick's momentum is not
     interrupted, short enough that the next swipe of the main image re-centres. */
  var RAIL_IDLE_MS = 900;

  function init(root) {
    if (!root || root.dataset.galleryReady === 'true') return;

    var track = root.querySelector('.core-pdp__grid');
    var thumbsWrap = root.querySelector('[data-pdp-thumbs]');
    if (!track) return;

    root.dataset.galleryReady = 'true';

    var tiles = Array.prototype.slice.call(track.querySelectorAll('.core-pdp__tile'));
    var thumbs = thumbsWrap
      ? Array.prototype.slice.call(thumbsWrap.querySelectorAll('[data-thumb-index]'))
      : [];

    var mobileQuery = window.matchMedia(MOBILE);
    var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    var bound = false;
    var scrollTimer = null;
    var lastActive = -1;

    var observer = null;
    var ratios = [];
    /* True while the shopper is operating the RAIL itself. Nothing may scroll
       the rail out from under a finger. */
    var railUserActive = false;
    var railIdleTimer = null;

    /* Which slide is in view? The one whose left edge is nearest the track's
       left edge. Reading positions rather than dividing scrollLeft by a assumed
       slide width means it stays correct if a tile is ever a different width,
       and it does not care about the gap. */
    function activeIndex() {
      var base = track.getBoundingClientRect().left;
      var best = 0;
      var bestDistance = Infinity;
      for (var i = 0; i < tiles.length; i++) {
        var d = Math.abs(tiles[i].getBoundingClientRect().left - base);
        if (d < bestDistance) {
          bestDistance = d;
          best = i;
        }
      }
      return best;
    }

    function paint(index) {
      if (index === lastActive) return;
      lastActive = index;
      for (var i = 0; i < thumbs.length; i++) {
        var on = Number(thumbs[i].getAttribute('data-thumb-index')) === index;
        /* aria-current is the whole state. The stylesheet keys off it, so the
           visual and the announced state cannot disagree. */
        if (on) {
          thumbs[i].setAttribute('aria-current', 'true');
        } else {
          thumbs[i].removeAttribute('aria-current');
        }
      }
    }

    /* Index lookup by attribute rather than array position. The two agree
       today, but the rail is rendered from a separate loop and a future gate on
       one of them would silently offset every centring by one. */
    function thumbAt(index) {
      for (var i = 0; i < thumbs.length; i++) {
        if (Number(thumbs[i].getAttribute('data-thumb-index')) === index) return thumbs[i];
      }
      return null;
    }

    /* CENTRE THE ACTIVE THUMB, CLAMPED AT THE RAIL'S OWN ENDS.

       The clamp is not a special case and there is no "first N" or "last N"
       branch: the target is simply the scroll position that would put this
       thumb's centre on the rail's centre, clipped to [0, scrollWidth -
       clientWidth]. At the start of the rail that clip yields 0 and at the end
       it yields the maximum, which is exactly the resting position the design
       asks for. The browser would clamp an out-of-range value identically; it
       is done here as well so the no-op check below is honest and so the
       expected value is computable in a test.

       force is for the one case where the rail must move even though the
       shopper just touched it: they tapped a thumbnail. */
    function centreThumb(index, force) {
      if (!thumbsWrap) return;
      if (railUserActive && !force) return;

      var thumb = thumbAt(index);
      if (!thumb) return;

      var railBox = thumbsWrap.getBoundingClientRect();
      var thumbBox = thumb.getBoundingClientRect();
      var delta = thumbBox.left + thumbBox.width / 2 - (railBox.left + railBox.width / 2);

      var target = thumbsWrap.scrollLeft + delta;
      var max = thumbsWrap.scrollWidth - thumbsWrap.clientWidth;
      if (target < 0) target = 0;
      if (target > max) target = max;

      /* Already there. Skipping matters: a smooth scroll of zero distance still
         cancels an in-flight one, which is what makes a rail judder. */
      if (Math.abs(target - thumbsWrap.scrollLeft) < 1) return;

      thumbsWrap.scrollTo({
        left: target,
        behavior: reducedMotion.matches ? 'auto' : 'smooth',
      });
    }

    /* THE ONE SOURCE OF TRUTH. Both detection routes call this and it is
       idempotent on the index, so the highlight and the rail position can never
       disagree and two sources cannot double-scroll. */
    function setActive(index) {
      if (index === lastActive) return;
      paint(index);
      centreThumb(index, false);
    }

    function onScroll() {
      if (scrollTimer) return;
      scrollTimer = window.setTimeout(function () {
        scrollTimer = null;
        setActive(activeIndex());
      }, SCROLL_THROTTLE_MS);
    }

    /* Most-visible tile wins. The callback only carries the tiles that crossed
       a threshold, so the ratio of every tile is kept and the maximum taken
       across all of them; deciding from the entries alone picks the wrong slide
       whenever the one leaving is reported and the one arriving is not. */
    function onIntersect(entries) {
      for (var i = 0; i < entries.length; i++) {
        var idx = tiles.indexOf(entries[i].target);
        if (idx >= 0) ratios[idx] = entries[i].intersectionRatio;
      }
      if (!mobileQuery.matches) return;

      var best = -1;
      var bestRatio = 0;
      for (var j = 0; j < tiles.length; j++) {
        var r = ratios[j] || 0;
        if (r > bestRatio) {
          bestRatio = r;
          best = j;
        }
      }
      if (best >= 0) setActive(best);
    }

    /* ---- rail ownership -------------------------------------------------
       While a finger is on the rail, or momentum from that finger is still
       running, the rail belongs to the shopper and sync keeps out of it. */
    function railTouched() {
      railUserActive = true;
      if (railIdleTimer) {
        window.clearTimeout(railIdleTimer);
        railIdleTimer = null;
      }
    }

    function railReleased() {
      if (railIdleTimer) window.clearTimeout(railIdleTimer);
      railIdleTimer = window.setTimeout(function () {
        railIdleTimer = null;
        railUserActive = false;
      }, RAIL_IDLE_MS);
    }

    /* A scroll on the rail while the shopper owns it re-arms the release, so a
       long flick does not hand control back mid-glide. */
    function onRailScroll() {
      if (railUserActive) railReleased();
    }

    /* Move the track to a slide. Left offset relative to the track's current
       scroll position, so it is correct wherever the track already sits. */
    function goToIndex(index, animate) {
      var tile = tiles[index];
      if (!tile) return;
      var delta = tile.getBoundingClientRect().left - track.getBoundingClientRect().left;
      track.scrollTo({
        left: track.scrollLeft + delta,
        behavior: animate && !reducedMotion.matches ? 'smooth' : 'auto',
      });
      paint(index);
      /* force: a thumbnail tap means the shopper's finger is on the rail, so
         the ownership guard would otherwise suppress the very centring the tap
         asked for. Safe from looping because nothing about scrolling the RAIL
         changes the main image, so this cannot feed back. */
      centreThumb(index, true);
    }

    function goToMediaId(mediaId) {
      for (var i = 0; i < tiles.length; i++) {
        if (String(tiles[i].getAttribute('data-media-id')) === String(mediaId)) {
          goToIndex(i, true);
          return;
        }
      }
      /* No tile for that media: do nothing, which matches the desktop rule.
         Moving to slide 0 would imply the colour has no photographs, and that
         is not what "no featured image set" means. */
    }

    function onThumbClick(event) {
      var button = event.target.closest('[data-thumb-index]');
      if (!button) return;
      goToIndex(Number(button.getAttribute('data-thumb-index')), true);
      /* Focus STAYS on the thumbnail. It is the control the shopper is
         operating, and the main tile is deliberately not a tab stop here, so
         moving focus into the track would strand it on an element that cannot
         be tabbed away from in the usual way. aria-current announces the
         change. */
    }

    /* ⚠️ SWALLOW THE TILE CLICK AT MOBILE, SO THE LIGHTBOX DOES NOT OPEN.
       assets/core-product.js binds a click listener to every [data-lb-index]
       tile to open the zoom dialog. That is right on desktop and wrong here:
       pinch to zoom is the mobile gesture, and a tap target that both advances
       a carousel and opens a modal fights itself.

       CAPTURE PHASE ON THE TRACK, which runs before the tile's own listener, so
       stopPropagation means the event never reaches it. Bound in setup() and
       removed in teardown(), so desktop is untouched: there the tile keeps its
       listener and tap to zoom works exactly as before.

       Measured before this existed: tapping the main image at 390px opened the
       lightbox. This is the fix for that. */
    function swallowTileClick(event) {
      if (!mobileQuery.matches) return;
      if (!event.target.closest('.core-pdp__tile')) return;
      event.stopPropagation();
    }

    function onGoto(event) {
      if (!mobileQuery.matches) return;
      var id = event && event.detail && event.detail.mediaId;
      if (id == null) return;
      goToMediaId(id);
    }

    function setup() {
      if (bound) return;
      bound = true;

      for (var i = 0; i < tiles.length; i++) tiles[i].setAttribute('tabindex', '-1');

      track.addEventListener('scroll', onScroll, { passive: true });
      track.addEventListener('click', swallowTileClick, true);
      if (thumbsWrap) {
        thumbsWrap.addEventListener('click', onThumbClick);
        thumbsWrap.addEventListener('pointerdown', railTouched, { passive: true });
        thumbsWrap.addEventListener('pointerup', railReleased, { passive: true });
        thumbsWrap.addEventListener('pointercancel', railReleased, { passive: true });
        /* touchstart/touchend as well as pointer events, not instead: a browser
           that fires only touch events would otherwise never set the guard, and
           one that fires both just re-arms the same timer. */
        thumbsWrap.addEventListener('touchstart', railTouched, { passive: true });
        thumbsWrap.addEventListener('touchend', railReleased, { passive: true });
        thumbsWrap.addEventListener('touchcancel', railReleased, { passive: true });
        thumbsWrap.addEventListener('scroll', onRailScroll, { passive: true });
      }

      if (window.IntersectionObserver) {
        ratios = [];
        observer = new window.IntersectionObserver(onIntersect, {
          root: track,
          threshold: [0, 0.25, 0.5, 0.75, 1],
        });
        for (var t = 0; t < tiles.length; t++) observer.observe(tiles[t]);
      }

      lastActive = -1;
      paint(activeIndex());
      centreThumb(activeIndex(), true);
    }

    function teardown() {
      if (!bound) return;
      bound = false;

      /* Put the tiles back in the tab order: on desktop they are the lightbox
         triggers. */
      for (var i = 0; i < tiles.length; i++) tiles[i].removeAttribute('tabindex');

      track.removeEventListener('scroll', onScroll);
      track.removeEventListener('click', swallowTileClick, true);
      if (thumbsWrap) {
        thumbsWrap.removeEventListener('click', onThumbClick);
        thumbsWrap.removeEventListener('pointerdown', railTouched);
        thumbsWrap.removeEventListener('pointerup', railReleased);
        thumbsWrap.removeEventListener('pointercancel', railReleased);
        thumbsWrap.removeEventListener('touchstart', railTouched);
        thumbsWrap.removeEventListener('touchend', railReleased);
        thumbsWrap.removeEventListener('touchcancel', railReleased);
        thumbsWrap.removeEventListener('scroll', onRailScroll);
      }

      if (observer) {
        observer.disconnect();
        observer = null;
      }
      ratios = [];

      for (var j = 0; j < thumbs.length; j++) thumbs[j].removeAttribute('aria-current');

      if (scrollTimer) {
        window.clearTimeout(scrollTimer);
        scrollTimer = null;
      }
      if (railIdleTimer) {
        window.clearTimeout(railIdleTimer);
        railIdleTimer = null;
      }
      railUserActive = false;
      lastActive = -1;
    }

    function sync() {
      if (mobileQuery.matches) {
        setup();
      } else {
        teardown();
      }
    }

    /* The goto listener is bound at every width and gates itself, so a colour
       chosen on desktop is ignored here and handled by core-product.js's own
       vertical scroll. */
    root.addEventListener('core:gallery:goto', onGoto);

    if (typeof mobileQuery.addEventListener === 'function') {
      mobileQuery.addEventListener('change', sync);
    } else if (typeof mobileQuery.addListener === 'function') {
      mobileQuery.addListener(sync);
    }

    sync();
  }

  function boot() {
    try {
      var roots = document.querySelectorAll('.core-pdp');
      for (var i = 0; i < roots.length; i++) init(roots[i]);
    } catch (e) {
      /* A gallery that fails to enhance is still a scrollable track of images.
         Never take the product page down for this. */
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
